/**
 * src/services/api.js
 * Central API service for the huhslCap React client.
 *
 * Set REACT_APP_API_URL to the full API root (including `/api`), e.g.
 *   http://localhost:5000/api
 * Restart the dev server after changing .env. Ensure server ALLOWED_ORIGINS
 * includes your app origin when API host differs from the SPA (CORS).
 *
 * If REACT_APP_API_URL is unset, falls back to `/api` (same-origin or CRA proxy).
 */
const envBase = process.env.REACT_APP_API_URL;
const BASE = envBase ? envBase.replace(/\/$/, '') : '/api';

// ─── TOKEN STORAGE ────────────────────────────────────────────────────────────
let _token = localStorage.getItem('huhslcap_token') || null;

export function setToken(t) {
  _token = t;
  if (t) localStorage.setItem('huhslcap_token', t);
  else   localStorage.removeItem('huhslcap_token');
}

export function getToken() { return _token; }

// ─── CORE FETCH ───────────────────────────────────────────────────────────────
async function req(method, path, body = null, { publicRequest = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (_token) headers['Authorization'] = `Bearer ${_token}`;

  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, opts);

  if (!publicRequest && res.status === 401) {
    setToken(null);
    window.location.href = '/';
    return;
  }

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
  return json;
}

const get    = (path, opts)        => req('GET',    path, null, opts);
const post   = (path, body, opts)  => req('POST',   path, body, opts);
const put    = (path, body, opts)  => req('PUT',    path, body, opts);

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const auth = {
  login:          (email, password) => post('/auth/login', { email, password }, { publicRequest: true }),
  me:             ()                => get('/auth/me'),
  logout:         ()                => post('/auth/logout'),
  forgotPassword: (email)           => post('/auth/forgot-password', { email }, { publicRequest: true }),
  resetPassword:  (token, password) => post('/auth/reset-password', { token, password }, { publicRequest: true }),
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export const dashboard = {
  kpis:        () => get('/dashboard/kpis'),
  obligations: () => get('/dashboard/obligations'),
  activity:    () => get('/dashboard/activity'),
  capitalMix:  () => get('/dashboard/capital-mix'),
  legacyPool:  () => get('/dashboard/legacy-pool'),
};

// ─── INVESTORS ────────────────────────────────────────────────────────────────
export const investors = {
  list:       (params = {}) => get('/investors?' + new URLSearchParams(params)),
  get:        (id)          => get(`/investors/${id}`),
  create:     (data)        => post('/investors', data),
  update:     (id, data)    => put(`/investors/${id}`, data),
  deposits:   (id)          => get(`/investors/${id}/deposits`),
  addDeposit: (id, data)    => post(`/investors/${id}/deposits`, data),
  statement:  (id)          => get(`/investors/${id}/statement`),
};

// ─── RETURNS ─────────────────────────────────────────────────────────────────
export const returns = {
  upcoming:  (days = 30)  => get(`/returns/upcoming?days=${days}`),
  history:   (limit = 50) => get(`/returns/history?limit=${limit}`),
  runCycle:  ()           => post('/returns/run-cycle'),
  markPaid:  (id)         => put(`/returns/${id}/mark-paid`),
};

// ─── INSTITUTIONS ─────────────────────────────────────────────────────────────
export const institutions = {
  list:                (params = {}) => get('/institutions?' + new URLSearchParams(params)),
  get:                 (id)          => get(`/institutions/${id}`),
  create:              (data)        => post('/institutions', data),
  update:              (id, data)    => put(`/institutions/${id}`, data),
  amortisation:        (id)          => get(`/institutions/${id}/amortisation`),
  generateAmortisation:(id)          => post(`/institutions/${id}/amortisation/generate`),
  markInstalment:      (id, rowId, data) => put(`/institutions/${id}/amortisation/${rowId}`, data),
  drawdowns:           (id)          => get(`/institutions/${id}/drawdowns`),
  addDrawdown:         (id, data)    => post(`/institutions/${id}/drawdowns`, data),
};

// ─── DEALS ────────────────────────────────────────────────────────────────────
export const deals = {
  list:                  (params = {}) => get('/deals?' + new URLSearchParams(params)),
  get:                   (id)          => get(`/deals/${id}`),
  create:                (data)        => post('/deals', data),
  update:                (id, data)    => put(`/deals/${id}`, data),
  reallocate:            (id, data)    => put(`/deals/${id}/allocate`, data),
  participation:         (id)          => get(`/deals/${id}/participation`),
  institutionAllocation: (id)          => get(`/deals/${id}/institution-allocation`),
  repayment:             (id, data)    => post(`/deals/${id}/repayment`, data),
  previewAllocation:     (amt, instId) => get(`/deals/allocation/preview?capitalRequired=${amt}${instId?`&pinnedInstitutionId=${instId}`:''}`),
  pools:                 ()            => get('/deals/allocation/pools'),
};

// ─── WITHDRAWALS ─────────────────────────────────────────────────────────────
export const withdrawals = {
  list:    (params = {}) => get('/withdrawals?' + new URLSearchParams(params)),
  get:     (id)          => get(`/withdrawals/${id}`),
  create:  (data)        => post('/withdrawals', data),
  approve: (id)          => put(`/withdrawals/${id}/approve`),
  reject:  (id, reason)  => put(`/withdrawals/${id}/reject`, { reason }),
  process: (id, data)    => put(`/withdrawals/${id}/process`, data),
};

// ─── MONDAY.COM ───────────────────────────────────────────────────────────────
export const monday = {
  status:      () => get('/monday/status'),
  deals:       () => get('/monday/deals'),
  sync:        () => post('/monday/sync'),
  logs:        () => get('/monday/logs'),
  mappings:    () => get('/monday/mappings'),
  setMappings: (data) => put('/monday/mappings', data),
};

// ─── AUDIT ────────────────────────────────────────────────────────────────────
export const audit = {
  list: (params = {}) => get('/audit?' + new URLSearchParams(params)),
  get:  (id)          => get(`/audit/${id}`),
};

// ─── SETTINGS ────────────────────────────────────────────────────────────────
export const settings = {
  get:         ()            => get('/settings'),
  update:      (data)        => put('/settings', data),
  users:       ()            => get('/settings/users'),
  createUser:  (data)        => post('/settings/users', data),
  updateUser:  (id, data)    => put(`/settings/users/${id}`, data),
  resetUserPassword: (id, password) => put(`/settings/users/${id}/password`, { password }),
};

// ─── REPORTS ─────────────────────────────────────────────────────────────────
export const reports = {
  investorStatement: (id, params = {}) => get(`/reports/legacy/investor-statement/${id}?${new URLSearchParams(params)}`),
  depositCycles:     (params = {})     => get('/reports/legacy/deposit-cycles?'     + new URLSearchParams(params)),
  returnAccrual:     ()                => get('/reports/legacy/return-accrual'),
  poolUtilisation:   ()                => get('/reports/legacy/pool-utilisation'),
  amortisation:      (params = {})     => get('/reports/institutional/amortisation?' + new URLSearchParams(params)),
  facilityUtil:      ()                => get('/reports/institutional/facility-utilisation'),
  deployedCapital:   ()                => get('/reports/portfolio/deployed-capital'),
  repaymentsDue:     (days = 60)       => get(`/reports/portfolio/repayments-due?days=${days}`),
};

// ─── FORMATTERS (shared with components) ─────────────────────────────────────
export const fmt = (n) =>
  'R ' + Math.round(Number(n || 0)).toLocaleString('en-ZA');

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const fmtPct  = (n) => `${Number(n || 0).toFixed(1)}%`;

export const journal = {
  preview:  (date) => get(`/journal/preview${date ? `?date=${date}` : ''}`),
  generate: (data) => post('/journal/generate', data),
  sendToXero: (data) => post('/journal/send-to-xero', data),
  history:    ()     => get('/journal/history'),
};

// ─── XERO ─────────────────────────────────────────────────────────────────────
export const xero = {
  status:      () => get('/xero/status'),
  connectUrl:  () => get('/xero/connect-url'),
  connect:     async () => {
    const { url } = await get('/xero/connect-url');
    if (!url) throw new Error('No Xero authorisation URL returned');
    window.location.href = url;
  },
  disconnect: () => post('/xero/disconnect'),
  refresh:    () => post('/xero/refresh'),
};
